import { Combobox, Input, Option } from "@fluentui/react-components"
import { NODE_STATUSES, type NodeStatus } from "@rema/shared/schemas/common"
import type { CreateNodeRequest, Node, UpdateNodeRequest } from "@rema/shared/schemas/nodes"
import { useMutation } from "@tanstack/react-query"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { AppDrawerForm } from "../../components/app-drawer-form"
import { FormField } from "../../components/form-field"
import { isApiError } from "../../lib/api"
import { applyFieldErrors, problemMessage } from "../../lib/problem"
import { nodesApi } from "./api"
import { nodesI18n } from "./i18n"

interface NodeFormValues {
  name: string
  base_url: string
  region: string
  weight: string
  capacity: string
  status: NodeStatus
}

const DEFAULT_VALUES: NodeFormValues = {
  name: "",
  base_url: "",
  region: "",
  weight: "100",
  capacity: "",
  status: "active",
}

const HTTP_URL_REGEX = /^https?:\/\/.+/i

export interface NodeFormDrawerProps {
  open: boolean
  // null = alta; un nodo = edición
  node: Node | null
  onClose: () => void
  onSaved: (mode: "created" | "updated") => void
}

// Drawer de alta/edición de nodo (docs/05 §3): validación inline por campo y mapeo
// de los errors[] del servidor a cada Field (lib/problem).
export const NodeFormDrawer = ({ open, node, onClose, onSaved }: NodeFormDrawerProps) => {
  const isEdit = node !== null
  const { control, handleSubmit, setError, reset } = useForm<NodeFormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: DEFAULT_VALUES,
  })

  // Al abrir, precarga los valores del nodo en edición o limpia para el alta.
  useEffect(() => {
    if (!open) return
    reset(
      node
        ? {
            name: node.name,
            base_url: node.base_url,
            region: node.region ?? "",
            weight: String(node.weight),
            capacity: node.capacity === null ? "" : String(node.capacity),
            status: node.status,
          }
        : DEFAULT_VALUES,
    )
  }, [open, node, reset])

  const mutation = useMutation({
    mutationFn: (values: NodeFormValues) => {
      if (isEdit && node) {
        const body: UpdateNodeRequest = {
          name: values.name.trim(),
          base_url: values.base_url.trim(),
          region: values.region.trim() === "" ? null : values.region.trim(),
          weight: Number(values.weight),
          capacity: values.capacity.trim() === "" ? null : Number(values.capacity),
          status: values.status,
        }
        return nodesApi.update(node.id, body)
      }
      const body: CreateNodeRequest = {
        name: values.name.trim(),
        base_url: values.base_url.trim(),
        ...(values.region.trim() === "" ? {} : { region: values.region.trim() }),
        weight: Number(values.weight),
        ...(values.capacity.trim() === "" ? {} : { capacity: Number(values.capacity) }),
      }
      return nodesApi.create(body)
    },
    onSuccess: () => onSaved(isEdit ? "updated" : "created"),
    onError: (error) => {
      // validation_error con errors[] → cada campo en rojo; el resto → MessageBar.
      if (isApiError(error)) applyFieldErrors(error.problem, setError)
    },
  })

  const onSubmit = handleSubmit((values) => mutation.mutate(values))

  // Solo se muestra en el MessageBar lo que NO se pudo mapear a un campo concreto.
  const formError =
    mutation.isError &&
    isApiError(mutation.error) &&
    (mutation.error.problem.errors?.length ?? 0) === 0
      ? problemMessage(mutation.error.problem)
      : null

  return (
    <AppDrawerForm
      open={open}
      title={isEdit ? nodesI18n.actions.edit : nodesI18n.actions.create}
      onSubmit={onSubmit}
      onClose={onClose}
      submitting={mutation.isPending}
      error={formError}
    >
      <FormField
        control={control}
        name="name"
        label={nodesI18n.fields.name}
        required
        rules={{ required: nodesI18n.validation.nameRequired }}
      >
        {(field, invalid) => (
          <Input {...field} placeholder={nodesI18n.fields.namePlaceholder} aria-invalid={invalid} />
        )}
      </FormField>

      <FormField
        control={control}
        name="base_url"
        label={nodesI18n.fields.baseUrl}
        required
        rules={{
          required: nodesI18n.validation.baseUrlRequired,
          validate: (value) =>
            HTTP_URL_REGEX.test(value.trim()) || nodesI18n.validation.baseUrlFormat,
        }}
      >
        {(field, invalid) => (
          <Input
            {...field}
            type="url"
            placeholder={nodesI18n.fields.baseUrlPlaceholder}
            aria-invalid={invalid}
          />
        )}
      </FormField>

      <FormField control={control} name="region" label={nodesI18n.fields.region}>
        {(field, invalid) => (
          <Input
            {...field}
            placeholder={nodesI18n.fields.regionPlaceholder}
            aria-invalid={invalid}
          />
        )}
      </FormField>

      <FormField
        control={control}
        name="weight"
        label={nodesI18n.fields.weight}
        hint={nodesI18n.fields.weightHint}
        rules={{
          validate: (value) => {
            const n = Number(value)
            return (Number.isInteger(n) && n >= 0) || nodesI18n.validation.weightRange
          },
        }}
      >
        {(field, invalid) => (
          <Input {...field} type="number" min={0} step={1} aria-invalid={invalid} />
        )}
      </FormField>

      <FormField
        control={control}
        name="capacity"
        label={nodesI18n.fields.capacity}
        hint={nodesI18n.fields.capacityHint}
        rules={{
          validate: (value) => {
            if (value.trim() === "") return true
            const n = Number(value)
            return (Number.isInteger(n) && n >= 0) || nodesI18n.validation.capacityRange
          },
        }}
      >
        {(field, invalid) => (
          <Input {...field} type="number" min={0} step={1} aria-invalid={invalid} />
        )}
      </FormField>

      {isEdit ? (
        <FormField control={control} name="status" label={nodesI18n.fields.status}>
          {(field) => (
            <Combobox
              selectedOptions={[field.value]}
              value={nodesI18n.status[field.value]}
              onOptionSelect={(_, data) => {
                if (data.optionValue) field.onChange(data.optionValue as NodeStatus)
              }}
              onBlur={field.onBlur}
            >
              {NODE_STATUSES.map((status) => (
                <Option key={status} value={status} text={nodesI18n.status[status]}>
                  {nodesI18n.status[status]}
                </Option>
              ))}
            </Combobox>
          )}
        </FormField>
      ) : null}
    </AppDrawerForm>
  )
}
